<template>
  <VSkeletonLoader
    grid-list-lg
    fluid
    height="auto"
    type="table-heading,card"
    :loading="isLoading"
    :class="{
      'mt-3': isLoading,
    }"
  >
    <VRow>
      <VCol cols="12">
        <h2 
          class="black--text"
          style="font-size: 30px;"
        >
          Overview Report
        </h2>
        <VCard class="py-4 px-6 mt-5 mb-8">
          <h3
            class="mb-4"
            style="font-size: 26px;"
          >
            Login Activity
          </h3>
          <div class="d-flex align-center">
            <div
              v-for="(bucket, index) in model.participant_logins.buckets"
              :key="index"
              :style="bucketStyle(bucket, index)"
            />
          </div>
          <VRow class="text-center mt-6 mb-2">
            <VCol class="py-0">
              <div 
                class="px-3 text-left"
                style="border-left: 3px solid #E7EAEE; display: inline-block;"
              >
                <div 
                  class="font-weight-bold"
                  style="font-size: 22px;"
                >
                  {{ model.participant_logins.total_users }}
                </div>
                <div 
                  class="text-body-2"
                  style="color: #64748B;"
                >
                  Total Users
                </div>
              </div>
            </VCol>
            <VCol
              v-for="(bucket, index) in model.participant_logins.buckets"
              class="py-0"
              :key="`bucket-${index}`"
            >
              <div 
                class="px-3 text-left"
                style="border-left: 3px solid #E7EAEE; display: inline-block;"
              >
                <div 
                  class="font-weight-bold"
                  style="font-size: 22px;"
                >
                  {{ bucket.count }}
                </div>
                <div 
                  class="d-flex align-center justify-center text-body-2"
                  style="color: #64748B;"
                >
                  <span :style="legendDotStyle(colors[index])" />
                  {{ bucket.label }}
                </div>
              </div>
            </VCol>
          </VRow>
        </VCard>
        <VCard 
          v-for="(chart, index) in charts"
          class="py-4 px-6 mt-5 mb-8"
          :key="index"
        >
          <h3
            class="mb-4"
            style="font-size: 26px;"
          >
            {{ chart.title }}
          </h3>
          <highcharts :options="chart.options" />
        </VCard>
      </VCol>
    </VRow>
  </VSkeletonLoader>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "ReportsIndex",
  data() {
    return {
      isLoading: false,
      model: {
        participant_logins: {},
        lessons_view_vs_submitted: [],
        forms_view_vs_submitted: [],
        files_viewed: [],
        faqs_viewed: [],
        hub_engagement: {
          daily_logins: [],
          daily_content_views: [],
          daily_content_submitted: [],
        },
      },
      colors: [
        "#0A5894",
        "#20BF55",
        "#FFC100",
        "#FF2A7A",
      ],
      filter: {
        start: null,
        end: null,
        programs: [],
      },
    };
  },
  computed: {
    charts() {
      return [
        {
          title: `${this.featureName('Program')} Engagement`,
          options: this.programsChartOptions,
        },
        {
          title: `${this.featureName('Lessons')} Viewed vs. Completed`,
          options: this.lessonsChartOptions,
        },
        {
          title: `${this.featureName('Deliverables')} Viewed vs. Submitted`,
          options: this.deliverablesChartOptions,
        },
        {
          title: `${this.featureName('Content')} Viewed`,
          options: this.primaryContentChartOptions,
        },
        {
          title: `${this.featureName('FAQs')} Viewed`,
          options: this.faqsChartOptions,
        },
      ];
    },
    programsChartOptions() {
      return {
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value:%d %b %Y}'
          },
          categories: this.programsChartDates.map((date) => Date.parse(date)),
        },
        series: [
          {
            name: 'Logins',
            color: this.colors[3],
            data: this.model.hub_engagement.daily_logins.map((detail) => detail.hub_logins),
          }, 
          {
            name: 'Content Views',
            color: this.colors[0],
            data: this.model.hub_engagement.daily_content_views.map((detail) => detail.hub_content_views),
          },
          {
            name: 'Content Created',
            color: this.colors[2],
            data: this.model.hub_engagement.daily_content_submitted.map((detail) => detail.hub_content_submitted),
          },
        ],
      };
    },
    lessonsChartOptions() {
      return {
        chart: {
          type: 'column'
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value:%d %b %Y}'
          },
          categories: this.model.lessons_view_vs_submitted.map((detail) => Date.parse(detail.date)),
        },
        tooltip: {
          xDateFormat: '%d %B %Y',
        },
        series: [
          {
            name: `${this.featureName('Lessons')} Viewed`,
            color: this.colors[3],
            data: this.model.lessons_view_vs_submitted.map((detail) => detail.viewed),
          },
          {
            name: `${this.featureName('Lessons')} Completed`,
            color: this.colors[0],
            data: this.model.lessons_view_vs_submitted.map((detail) => detail.submitted),
          },
        ],
      };
    },
    deliverablesChartOptions() {
      return {
        chart: {
          type: 'column'
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value:%d %b %Y}'
          },
          categories: this.model.forms_view_vs_submitted.map((detail) => Date.parse(detail.date)),
        },
        tooltip: {
          xDateFormat: '%d %B %Y',
        },
        series: [
          {
            name: `${this.featureName('Deliverable')} Viewed`,
            color: this.colors[2],
            data: this.model.forms_view_vs_submitted.map((detail) => detail.viewed),
          },
          {
            name: `${this.featureName('Deliverable')} Submitted`,
            color: this.colors[1],
            data: this.model.forms_view_vs_submitted.map((detail) => detail.submitted),
          },
        ],
      };
    },
    primaryContentChartOptions() {
      return {
        chart: {
          type: 'area'
        },
        legend: {
          enabled: false,
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value:%d %b %Y}'
          },
          categories: this.model.files_viewed.map((detail) => Date.parse(detail.date)),
        },
        tooltip: {
          xDateFormat: '%d %B %Y',
        },
        plotOptions: {
          area: {
            marker: {
              enabled: this.model.files_viewed.length === 1,
              states: {
                hover: {
                  enabled: true,
                }
              }
            }
          }
        },
        series: [
          {
            color: this.colors[0],
            data: this.model.files_viewed.map((detail) => detail.count),
          },
        ],
      };
    },
    faqsChartOptions() {
      return {
        chart: {
          type: 'area'
        },
        legend: {
          enabled: false,
        },
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value:%d %b %Y}'
          },
          categories: this.model.faqs_viewed.map((detail) => Date.parse(detail.date)),
        },
        tooltip: {
          xDateFormat: '%d %B %Y',
        },
        plotOptions: {
          area: {
            marker: {
              enabled: this.model.faqs_viewed.length === 1,
              states: {
                hover: {
                  enabled: true,
                }
              }
            }
          }
        },
        series: [
          {
            color: this.colors[2],
            data: this.model.faqs_viewed.map((detail) => detail.count),
          },
        ],
      };
    },
    totalParticipants() {
      return this.model.participant_logins.buckets.reduce((total, bucket) => total + bucket.count, 0);
    },
    programsChartDates() {
      const dates = [];
      Object.keys(this.model.hub_engagement).map((category) => {
        this.model.hub_engagement[category].map((detail) => dates.push(detail.date));
      });
      return [...new Set(dates)].sort();
    }
  },
  created() {
    this.getTeamReport().then((result) => {
      result.related_programs.unshift({
        id: 'none',
        title: "None",
      });
      this.$bus.$emit("reportDetails", result);
    });
    this.$bus.$on('filterReport', (dateRange, programs) => {
      if(dateRange == 'all') {
        this.filter.start = null;
        this.filter.end = null;
      } else {
        this.filter.start = this.$timezone.moment().subtract(dateRange, 'days').format('YYYY-MM-DD');
        this.filter.end = this.$timezone.moment().format('YYYY-MM-DD');
      }
      this.report = null;
      this.filter.programs = programs;
      this.getTeamReport();
    });
  },
  beforeDestroy() {
    this.$bus.$off('filterReport');
  },
  methods: {
    ...mapActions({
      doGetTeamReport: "report/team",
    }),
    getTeamReport() {
      this.isLoading = true;
      const params = {
        ...this.filter,
      };
      return this.doGetTeamReport(params).then((result) => {
        this.model = result;
        this.isLoading = false;
        return result;
      });
    },
    bucketStyle(bucket, index) {
      const percent = (bucket.count / this.totalParticipants) * 100;
      return {
        background: this.colors[index],
        width: percent + '%',
        height: '54px',
        transition: 'width 0.3s',
      };
    },
    legendDotStyle(color) {
      return {
        display: 'inline-block',
        width: '12px',
        height: '12px',
        'border-radius': '28%',
        'background-color': color,
        'margin-right': '6px',
      };
    },
  },
};
</script>